import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { deleteApiOrdersByEncryptedOrderIdProducts, postApiOrdersByEncryptedOrderIdProducts } from 'api/generated';
import { getApiEventsByEventIdProductsQueryKey } from 'api/generated/@tanstack/react-query.gen';
import type { OrderResponse } from 'api/generated/types.gen';
import { useExchangeAddGuard } from 'components/common/exchange/exchange-add-guard';
import { useAppState } from 'lib/app-state';
import { toastError, unexpectedErrorToast } from 'lib/helpers/error-toast-helpers';
import { handleOrderNotFound } from 'lib/helpers/order-helpers';
import { getEventProductLines, type OrderProductLine } from 'lib/helpers/product-helpers';
import { enqueueProductChange, holdProductChange, latestProductOrder, registerProductChangeFlush } from 'lib/hooks/use-product-changes';

/** How long the stepper rests before its clicks are sent as one change. */
const sendDelayMs = 500;
type Pending = Record<number, number>;
const omit = (pending: Pending, variantId: number): Pending => {
  const {
    [variantId]: _,
    ...rest
  } = pending;
  return rest;
};

/**
 * Changes how many units of a product variant the order holds for an event.
 * A change shows at once in `pending` (the quantity asked for, by variant id)
 * and is sent once the stepper rests, so quick clicks go out as one request.
 * Units are added through the add endpoint, so they pass the same sale-status
 * and stock checks, and removed by their row ids. A rejected change is undone
 * and the backend's problem shown as-is. Changes wait their turn behind every
 * other product change on the order.
 */
export const useChangeProductQuantity = (eventId: number) => {
  const appState = useAppState();
  const queryClient = useQueryClient();
  const {
    guardAdd
  } = useExchangeAddGuard();
  const order = appState.eventData.order;
  const [pending, setPendingState] = useState<Pending>({});
  // Timers and requests read the latest values rather than a render's.
  const pendingRef = useRef<Pending>({});
  const orderRef = useRef(order);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  // Releases for the changes held while their stepper rests, by variant.
  const held = useRef(new Map<number, () => void>());
  // Counts the buyer's changes by variant, telling whether one came in while
  // a request was out.
  const revisions = useRef(new Map<number, number>());
  useEffect(() => {
    orderRef.current = order;
  }, [order]);
  const setPending = (update: (prev: Pending) => Pending) => {
    pendingRef.current = update(pendingRef.current);
    setPendingState(pendingRef.current);
  };
  const ask = (variantId: number, quantity: number) => {
    revisions.current.set(variantId, (revisions.current.get(variantId) ?? 0) + 1);
    setPending(prev => ({
      ...prev,
      [variantId]: quantity
    }));
  };

  // Drops the quantities the order now holds, and the sent one unless the
  // buyer changed it since: the order is the answer to it either way.
  const settlePending = (order: OrderResponse, sentVariantId: number, sentRevision?: number) => setPending(prev => {
    const lines = getEventProductLines(order, eventId);
    return Object.fromEntries(Object.entries(prev).filter(([key, quantity]) => {
      const variantId = Number(key);
      if (variantId === sentVariantId && revisions.current.get(variantId) === sentRevision) {
        return false;
      }
      const held = lines.find(l => l.productVariantId === variantId)?.quantity ?? 0;
      return held !== quantity;
    }));
  });

  // Remaining counts move with every change, and a rejection may come from
  // stock that ran out since the list was loaded.
  const refreshOffered = () => queryClient.invalidateQueries({
    queryKey: getApiEventsByEventIdProductsQueryKey({
      path: {
        eventId
      }
    })
  });
  const send = async (variantId: number): Promise<{
    order: OrderResponse | undefined;
  } | void> => {
    const target = pendingRef.current[variantId];
    if (target == null) return;
    const order = latestProductOrder(orderRef.current);
    if (!order) {
      setPending(prev => omit(prev, variantId));
      return;
    }
    const line = getEventProductLines(order, eventId).find(l => l.productVariantId === variantId);
    const delta = target - (line?.quantity ?? 0);
    if (delta === 0) {
      setPending(prev => omit(prev, variantId));
      return;
    }
    const sentRevision = revisions.current.get(variantId);
    const path = {
      encryptedOrderId: order.encryptedId ?? '0'
    };
    try {
      const result: any = delta > 0 ? await postApiOrdersByEncryptedOrderIdProducts({
        path,
        body: {
          eventId,
          items: [{
            productVariantId: variantId,
            quantity: delta
          }]
        }
      }) : await deleteApiOrdersByEncryptedOrderIdProducts({
        path,
        // The newest units go first, leaving the longest-held ones.
        body: {
          encryptedOrderProductIds: (line?.encryptedOrderProductIds ?? []).slice(delta)
        }
      });
      if (handleOrderNotFound(result, () => appState.setEventData(prev => ({
        ...prev,
        order: undefined
      })))) {
        timers.current.forEach(clearTimeout);
        timers.current.clear();
        held.current.forEach(release => release());
        held.current.clear();
        setPending(() => ({}));
        toastError(result.response?.data);
        return {
          order: undefined
        };
      }
      refreshOffered();
      if (!result.data) {
        setPending(prev => omit(prev, variantId));
        toastError(result.response?.data);
        return;
      }
      const nextOrder = result.data as OrderResponse;
      orderRef.current = nextOrder;
      appState.setEventData(prev => ({
        ...prev,
        order: nextOrder
      }));
      settlePending(nextOrder, variantId, sentRevision);
      return {
        order: nextOrder
      };
    } catch (error) {
      console.error('Change product quantity error', error);
      setPending(prev => omit(prev, variantId));
      unexpectedErrorToast();
    }
  };
  const sendNow = (variantId: number) => {
    clearTimeout(timers.current.get(variantId));
    timers.current.delete(variantId);
    void enqueueProductChange(() => send(variantId));
    // Released once queued, so the change never reads as settled in between.
    held.current.get(variantId)?.();
    held.current.delete(variantId);
  };

  // Leaving the cart, or heading to checkout, sends whatever the stepper
  // still holds.
  useEffect(() => {
    const running = timers.current;
    const flush = () => [...running.keys()].forEach(sendNow);
    const unregister = registerProductChangeFlush(flush);
    return () => {
      unregister();
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setQuantity = (line: OrderProductLine, quantity: number) => {
    const variantId = line.productVariantId;
    const apply = () => {
      ask(variantId, quantity);
      if (!held.current.has(variantId)) {
        held.current.set(variantId, holdProductChange());
      }
      clearTimeout(timers.current.get(variantId));
      timers.current.set(variantId, setTimeout(() => sendNow(variantId), sendDelayMs));
    };
    if (quantity > line.quantity) guardAdd(apply);else apply();
  };
  const removeLine = (line: OrderProductLine) => {
    ask(line.productVariantId, 0);
    sendNow(line.productVariantId);
  };
  return {
    setQuantity,
    removeLine,
    pending
  };
};